import React, { useMemo } from 'react';

import { di } from 'react-magnetic-di';
import { graphql, useFragment } from 'react-relay';

import { Banner } from '@atlaskit/navigation-system/layout/banner';
import type { GlobalSideNavigationProps } from '@atlassian/global-side-navigation/common/types';
import { GlobalSideNavigation } from '@atlassian/global-side-navigation/GlobalSideNavigation';
import type { GlobalTopNavigationProps } from '@atlassian/global-top-navigation/common/types';
import { GlobalTopNavigation } from '@atlassian/global-top-navigation/GlobalTopNavigation';

import type { AnyGlobalRootQueryData, AppChromeProps, BannerProps } from '../common/types';
import {
	InternalAppChromeContext,
	type InternalAppChromeContextType,
	useInternalAppChromeContext,
} from '../controllers/useInternalAppChromeContext';
import type { AppChrome$key } from './__generated__/AppChrome.graphql';

/**
 * AppChrome renders the app-level navigation slots (Banner, TopNav and SideNav)
 * and wires GlobalTopNavigation (GTN) as the base implementation of the TopNav.
 *
 * The `queryRef` resolved by `useRelayQuery` is injected into the base TopNav
 * and SideNav. Consumers customize either component via its slot render prop.
 * The same raw, `Query`-level `queryRef` is also handed to both slots, so a
 * consumer whose own `useRelayQuery` spreads additional fragments (alongside
 * `...AppChrome`) can unmask its own data with `useFragment` inside the slot.
 * The platform-owned fragment reference remains bound to the base navigation
 * components. When no `AppHomeSlot` is supplied, GTN renders a default App Home
 * from `app`/`appName`.
 */
export function AppChrome<Q extends AnyGlobalRootQueryData = AnyGlobalRootQueryData>(
	props: AppChromeProps<Q>,
): React.JSX.Element {
	const {
		app,
		appLabel,
		closeChatPanel,
		closeHelpPanel,
		customizationStore,
		intlMode,
		isAnonymous = false,
		isChatPanelOpen,
		isHelpPanelOpen,
		networkStorageParams,
		openChatPanel,
		openHelpPanel,
		BannerSlot,
		SideNavSlot,
		TopNavSlot,
		useRelayQuery,
	} = props;

	// The consumer-provided hook returns the query result whose only node is
	// `...AppChrome`. Any query variables (e.g. the ARIs for personal navigation
	// customization) are baked into the consumer's `useRelayQuery` implementation.
	const queryRef = useRelayQuery();

	// Read the `AppChrome` fragment to unmask the child fragment spreads (e.g.
	// `...GlobalTopNavigation`). The resulting reference is what GTN/GSN expect
	// as their own `queryRef`.
	//
	// No local @argumentDefinitions on the `...GlobalTopNavigation` spread:
	// child fragments read ordinary query-level variables, which are in scope
	// for every spread into the same operation. `collabContextRoutingAri` is
	// required — Notifications is a default GTN slot, so the unread-count
	// field is always selected. `appSwitcherProductAri`/
	// `skipAppSwitcherUnitProfile` (App Home's unit title, see NAVX-3388/
	// NAVX-5411) only need declaring if the app wants the unit profile.
	const appChromeData = useFragment<AppChrome$key>(
		graphql`
			fragment AppChrome on Query {
				...GlobalTopNavigation
				...GlobalSideNavigation
			}
		`,
		queryRef,
	);

	// The values the base nav components need from this scope travel by context,
	// not by closure, so those components can keep a stable identity. See the
	// note on `BaseSideNav`.
	const internalAppChromeContext = useMemo<InternalAppChromeContextType>(
		() => ({
			app,
			appChromeData,
			appLabel,
			closeChatPanel,
			closeHelpPanel,
			customizationStore,
			intlMode,
			isAnonymous,
			isChatPanelOpen,
			isHelpPanelOpen,
			networkStorageParams,
			openChatPanel,
			openHelpPanel,
		}),
		[
			app,
			appChromeData,
			appLabel,
			closeChatPanel,
			closeHelpPanel,
			customizationStore,
			intlMode,
			isAnonymous,
			isChatPanelOpen,
			isHelpPanelOpen,
			networkStorageParams,
			openChatPanel,
			openHelpPanel,
		],
	);

	return (
		// A context provider renders no DOM, so the layout slots below remain
		// direct children of `Root`'s grid.
		<InternalAppChromeContext.Provider value={internalAppChromeContext}>
			{BannerSlot && <BannerSlot BannerComponent={BaseBanner} />}
			{TopNavSlot !== null &&
				(TopNavSlot ? (
					/* Pass the "unmasked"/root queryRef so that the caller can access the root-query's fragments (the caller
					   knows what data to pass to the components).
					 */
					<TopNavSlot TopNavComponent={BaseTopNav} queryRef={queryRef} />
				) : (
					<BaseTopNav />
				))}
			{SideNavSlot !== null &&
				(SideNavSlot ? (
					<SideNavSlot SideNavComponent={BaseSideNav} queryRef={queryRef} />
				) : (
					<BaseSideNav />
				))}
		</InternalAppChromeContext.Provider>
	);
}

/**
 * The base TopNav, handed to `TopNavSlot` as its `TopNavComponent`.
 *
 * Declared at module scope for the same reason as {@link BaseSideNav}.
 */
function BaseTopNav(wrapperProps: Omit<Partial<GlobalTopNavigationProps>, 'queryRef'>) {
	di(GlobalTopNavigation);

	const {
		app,
		appLabel,
		closeChatPanel,
		closeHelpPanel,
		customizationStore,
		appChromeData,
		intlMode,
		isAnonymous,
		isChatPanelOpen,
		isHelpPanelOpen,
		networkStorageParams,
		openChatPanel,
		openHelpPanel,
	} = useInternalAppChromeContext('BaseTopNav');

	// GTN requires a non-null `queryRef`. When no navigation data has been
	// loaded (the default/no-op `useRelayQuery`), don't render the base
	// TopNav — a customizing consumer is expected to supply its own
	// `useRelayQuery` (and therefore a valid fragment reference).
	if (!appChromeData) {
		return null;
	}

	return (
		<GlobalTopNavigation
			app={app}
			appName={appLabel}
			closeChatPanel={closeChatPanel}
			customizationStore={customizationStore}
			intlMode={intlMode}
			networkStorageParams={networkStorageParams}
			testId="global-root-topnav"
			{...wrapperProps}
			closeHelpPanel={closeHelpPanel}
			isAnonymous={isAnonymous}
			isChatPanelOpen={isChatPanelOpen}
			isHelpPanelOpen={isHelpPanelOpen}
			openChatPanel={openChatPanel}
			openHelpPanel={openHelpPanel}
			queryRef={appChromeData}
		/>
	);
}

/**
 * The base SideNav, handed to `SideNavSlot` as its `SideNavComponent`.
 *
 * Declared at module scope — deliberately NOT built per render inside
 * `AppChrome` — so its identity is stable for the lifetime of the app. A
 * component created per render (e.g. with `useCallback` closing over
 * `appChromeData`) gets a new identity whenever the app chrome query
 * reference changes, and because a slot renders it as `<SideNavComponent />`
 * that new identity is a new element *type*. React unmounts and remounts a
 * subtree whose type changed, so the whole side nav — and any UI state it owns,
 * such as expanded sections and scroll position — would be thrown away on every
 * navigation that produces a new query reference. Apps whose chrome query has
 * route-derived variables (an issue key, a project key) hit that on ordinary
 * navigation. Taking the varying data from context instead means new data
 * re-renders the side nav rather than replacing it (NAVX-5414).
 */
function BaseSideNav(wrapperProps: Omit<Partial<GlobalSideNavigationProps>, 'queryRef'>) {
	di(GlobalSideNavigation);

	const {
		app,
		appChromeData,
		appLabel,
		customizationStore,
		intlMode,
		isAnonymous,
		networkStorageParams,
	} = useInternalAppChromeContext('BaseSideNav');

	return (
		<GlobalSideNavigation
			app={app}
			appLabel={appLabel}
			customizationStore={customizationStore}
			// See the note on `BaseTopNav`: both halves of the chrome get the same
			// value so they cannot end up translated differently.
			intlMode={intlMode}
			networkStorageParams={networkStorageParams}
			testId="global-root-sidenav"
			forYou={{}}
			items={{}}
			{...wrapperProps}
			isAnonymous={isAnonymous}
			// Supplying `queryRef` tells GSN to read its customization data from
			// the `...GlobalSideNavigation` spread in our own query rather than
			// expecting the caller to wrap it in
			// `GlobalSideNavigationAGGRelayConnection`. It is set after the
			// spread so a slot cannot accidentally decouple GSN from GRT's query.
			queryRef={appChromeData ?? undefined}
		/>
	);
}

function BaseBanner(wrapperProps: BannerProps) {
	return <Banner {...wrapperProps} />;
}
